"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function ArchiveHeaderConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="mb-8 flex flex-col items-center text-center md:items-start md:text-left">
        <div className="mb-6 flex items-center gap-2">
          <div className="h-5 w-16 animate-pulse rounded bg-muted"></div>
          <div className="h-4 w-4 animate-pulse rounded bg-muted"></div>
          <div className="h-5 w-24 animate-pulse rounded bg-muted"></div>
        </div>
        <div className="h-12 w-64 animate-pulse rounded-lg bg-muted sm:w-96"></div>
      </div>
    );
  }

  if (error || links.length === 0) {
    return (
      <div className="mb-8 flex flex-col items-center text-center md:items-start md:text-left">
        <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          Shop
        </h1>
      </div>
    );
  }

  const lastIndex = links.length - 1;
  const title = links[lastIndex].title;

  return (
    <div className="mb-8 flex flex-col items-center text-center md:items-start md:text-left">
      <Breadcrumb className="mb-6">
        <BreadcrumbList className="justify-center md:justify-start">
          {links.map((link, index) => {
            const isLast = index === lastIndex;
            return (
              <React.Fragment key={link.href}>
                <BreadcrumbItem data-index={index}>
                  {isLast ? (
                    <BreadcrumbPage className="text-sm font-medium text-foreground">
                      {link.title}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link
                        to={link.href}
                        className="text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {link.title}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </React.Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
        {title}
      </h1>
    </div>
  );
}

function ArchiveCategoriesConsumer() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="flex flex-wrap justify-center gap-3 md:justify-start">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-10 w-28 animate-pulse rounded-full bg-muted"
          />
        ))}
      </div>
    );
  }

  if (error || terms.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap justify-center gap-3 md:justify-start">
      {terms.map((term, i) => (
        <div key={term.id} data-index={i}>
          {term.link ? (
            <Link
              to={term.link}
              className="inline-flex items-center justify-center rounded-full border border-border bg-card px-5 py-2 text-sm font-medium text-card-foreground shadow-sm transition-all hover:border-primary hover:text-primary hover:shadow-md"
            >
              {term.name}
            </Link>
          ) : (
            <span className="inline-flex items-center justify-center rounded-full border border-border bg-card px-5 py-2 text-sm font-medium text-card-foreground shadow-sm">
              {term.name}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ArchiveTitleSection() {
  
  const current_wp_term_query_params =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPTermQueryParams()
      : { taxonomy: "product_cat" };
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section
      id="archive-title-section"
      className="bg-background py-12 md:py-16 lg:py-20 border-b border-border"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-5xl">
          <ShopBreadcrumbsProvider>
            <ArchiveHeaderConsumer />
          </ShopBreadcrumbsProvider>

          <EcommerceTermsProvider wp_term_query={wp_term_query}>
            <ArchiveCategoriesConsumer />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>
  );
}